
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Product } from '@/types';
import { useProductContext } from '@/context/ProductContext';
import { fetchAllProducts, addProduct, updateProduct, deleteProduct, subscribeToProductChanges } from '@/services/productService';
import { supabase } from '@/integrations/supabase/client';

export const useProducts = () => {
  const {
    products,
    setProducts,
    isLoading,
    setIsLoading,
    selectedProduct,
    setSelectedProduct,
    setIsEditSheetOpen,
    setIsDeleteDialogOpen,
  } = useProductContext();
  
  const [connectionError, setConnectionError] = useState(false);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      setConnectionError(false);
      
      const products = await fetchAllProducts();
      setProducts(products);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error("Erreur lors du chargement des produits: Problème de connexion à Supabase");
      setConnectionError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddProduct = async (newProduct: Omit<Product, "id" | "status" | "createdAt" | "createdBy">) => {
    try {
      const addedProduct = await addProduct(newProduct);
      setProducts([addedProduct, ...products]);
      toast.success("Produit ajouté avec succès");
    } catch (error) {
      console.error('Error adding product:', error);
      
      // More detailed error messages
      if (error.code === '23505') {
        toast.error("Ce produit existe déjà");
      } else if (error.code === '23503') {
        toast.error("Référence invalide");
      } else if (error.code === '42P01') {
        toast.error("Table introuvable. Vérifiez la connexion à Supabase");
      } else {
        toast.error(`Erreur lors de l'ajout du produit: ${error.message}`);
      }
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setIsEditSheetOpen(true);
  };

  const handleUpdateProduct = async (updatedProduct: Product) => {
    try {
      await updateProduct(updatedProduct);
      setProducts(products.map(p => 
        p.id === updatedProduct.id ? updatedProduct : p
      ));
      toast.success("Produit mis à jour avec succès");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du produit");
      console.error('Error updating product:', error);
    }
  };

  const handleDeleteProduct = (product: Product) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProduct) return;

    try {
      await deleteProduct(selectedProduct.id);
      setProducts(products.filter(p => p.id !== selectedProduct.id));
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
      toast.success("Produit supprimé avec succès");
    } catch (error) {
      toast.error("Erreur lors de la suppression du produit");
      console.error('Error deleting product:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    
    // Set up real-time subscription for products table updates
    const channel = subscribeToProductChanges(fetchProducts);

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    products,
    isLoading,
    connectionError,
    handleAddProduct,
    handleEditProduct,
    handleUpdateProduct,
    handleDeleteProduct,
    handleConfirmDelete,
    refreshProducts: fetchProducts
  };
};
